import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import PillarSetupWizard from "@/components/PillarSetupWizard";
import { useAuth } from "@/context/AuthContext";
import { SELF_EXPRESSION_LINE } from "@/data/pillars";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-white/40 flex-col fixed inset-y-0 left-0">
        <Sidebar />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-background">
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        {/* Sticky glass header with constant self-expression line */}
        <header className="sticky top-0 z-20 backdrop-blur-md bg-background/70 border-b border-border">
          <div className="flex items-center gap-4 px-5 md:px-8 h-16">
            <button
              className="md:hidden p-2 -ml-2 text-foreground"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <p
              className="flex-1 font-serif text-base md:text-lg text-foreground/90 truncate"
              data-testid="self-expression-line"
            >
              {SELF_EXPRESSION_LINE}
            </p>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-secondary transition-colors duration-200"
                  data-testid="user-menu-button"
                >
                  <span className="w-8 h-8 rounded-full bg-foreground text-background text-xs font-medium flex items-center justify-center">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden sm:block text-sm text-foreground/80 max-w-[140px] truncate">
                    {user?.name}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                    <span className="text-xs text-muted-foreground font-normal mt-1 capitalize">
                      {user?.role}
                      {user?.age != null ? ` · age ${user.age}` : ""}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      {user && user.setup_completed === false && <PillarSetupWizard />}
    </div>
  );
}
